import { useQuery } from '@tanstack/react-query';
import {
    BlockComponent,
    ComponentSettingsManageFilters,
    ContentSettings,
    ManageFiltersRelevantValuesResponse,
} from '@report-content';
import { reportAPI } from 'api/report';
import { IFilterColumn } from 'components/report-content/components/manage-filters';
import getFilterConditionRules from 'components/report-content/components/manage-filters/helpers/getFilterConditionRules';

// Fetches, per reference_name, the values still allowed given the currently selected filters.
// Only active when the block's "only display relevant values" setting is on.
export function useFiltersRelevantValues(
    contentSettings: ContentSettings,
    component: BlockComponent<ComponentSettingsManageFilters>,
    filterColumns: Array<IFilterColumn>,
) {
    const enabled = component.settings.only_display_relevant_values == 'Y';

    // Only dropdown/multiselect filters narrow relevant values.
    // Sorted by reference_name so the key is stable regardless of ordering,
    const relevantSelections = filterColumns
        .filter((filter) => filter.filterType == 'dropdown' || filter.filterType == 'multiselect')
        .map((filter) => [
            filter.reference_name,
            // A default value of [""] means "nothing selected", same as [] - without dropping it,
            // the key would treat the two as different states and refetch needlessly.
            [...filter.savedValues].filter((v) => v !== '').sort(),
        ])
        .sort((a, b) => (a[0] as string).localeCompare(b[0] as string));

    // With nothing selected there's nothing to narrow by - no rules would be sent anyway, so
    // skip the request instead of asking the server to confirm there's no restriction.
    const hasSelection = relevantSelections.some(([, values]) => (values as Array<any>).length > 0);
    const queryEnabled = enabled && hasSelection;

    const { data, isFetching } = useQuery<ManageFiltersRelevantValuesResponse, Error>({
        queryKey: [`report_filters_relevant_values_${component.datasetRendererBlockComponentId}`, relevantSelections],
        queryFn: () => {
            const rules = getFilterConditionRules(component, filterColumns);
            return reportAPI.getFiltersRelevantValues(
                contentSettings.elementId,
                contentSettings.segmentValueId,
                rules ? JSON.stringify(rules) : null,
            );
        },
        enabled: queryEnabled,
        staleTime: Infinity,
    });

    return {
        // Discarded once the setting is off, so a stale narrowed list can't outlive it.
        relevantValues: queryEnabled ? (data?.values ?? null) : null,
        isLoadingRelevantValues: queryEnabled && isFetching,
    };
}
